import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";
import { AlertBannerContext } from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../Graphql/utils";
import {
  changePasswordQuery,
  getProfileQuery,
  updateProfileQuery,
  uploadProfileImageQuery
} from "../../Graphql/querries/profileQuery";
import {
  ChangePasswordInput,
  ChangePasswordResponse,
  GetProfileResponse,
  UpdateProfileFormData,
  UpdateProfileResponse,
  UploadProfileImageResponse
} from "../../components/Profile/types/profileTypes";
import {
  API_RESPONSE_CODE,
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY,
  KNOWN_API_ERROR_CODES
} from "@kivunova/kivufrontendcommon";
import { setProfileData } from "../../store/slices/profileSlice";
import {
  setUserDetails,
  setProfileLoadError
} from "../../store/slices/userSlice";
import { useDispatch, useSelector } from "react-redux";

import { RootState } from "../../store/store";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

export const useGetProfile = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const userId = userDetails?.id;
  const companyId = userDetails?.companyId;
  const storeId = userDetails?.storeId;

  return useMutation<GetProfileResponse, Error>({
    mutationKey: ["profile"],

    mutationFn: () => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching user profile",
        buildLogContext("profile", "get-profile", userId, companyId, storeId)
      );

      return fetchGraphQL(getProfileQuery, { input: {} }, setAlert);
    },

    onSuccess: (response) => {
      if (response.getProfile.code === API_RESPONSE_CODE.OK) {
        const user = response.getProfile.user;
        dispatch(setProfileData(user));
        dispatch(setUserDetails(user));
        dispatch(setProfileLoadError(false));

        // ✅ Success — use IDs from the response since they are now confirmed
        sendAppLog(
          LOG_LEVEL.INFO,
          "Profile fetched successfully",
          buildLogContext(
            "profile",
            "get-profile",
            user?.id,
            user?.companyId,
            undefined
          )
        );
      } else {
        dispatch(setProfileLoadError(true));

        // ⚠️ Expected API/business failure
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Get profile rejected by API",
          buildLogContext(
            "profile",
            "get-profile",
            userId,
            companyId,
            storeId,
            { apiCode: String(response.getProfile.code) }
          )
        );

        setAlert({
          code:
            (response.getProfile.code as ALERT_BANNER_CODE) ||
            ALERT_BANNER_CODE.INTERNAL_ERROR,
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR
        });
      }
    },

    onError: (error) => {
      dispatch(setProfileLoadError(true));

      // ❌ Unexpected runtime/network error
      captureAppException(
        "Get profile failed with unexpected error",
        error,
        buildLogContext("profile", "get-profile", userId, companyId, storeId)
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};

// ─── useUpdateProfile ─────────────────────────────────────────────────────────

export const useUpdateProfile = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const userId = userDetails?.id;
  const companyId = userDetails?.companyId;
  const storeId = userDetails?.storeId;

  return useMutation<UpdateProfileResponse, Error, UpdateProfileFormData>({
    mutationKey: ["updateProfile"],

    mutationFn: (updates) => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started updating user profile",
        buildLogContext("profile", "update-profile", userId, companyId, storeId)
      );

      return fetchGraphQL(updateProfileQuery, { input: { updates } }, setAlert);
    }
  });
};

// ─── useChangePassword ────────────────────────────────────────────────────────

export const useChangePassword = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const userId = userDetails?.id;
  const companyId = userDetails?.companyId;
  const storeId = userDetails?.storeId;

  return useMutation<ChangePasswordResponse, Error, ChangePasswordInput>({
    mutationKey: ["changePassword"],

    mutationFn: async (
      input: ChangePasswordInput
    ): Promise<ChangePasswordResponse> => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started changing password",
        buildLogContext("auth", "change-password", userId, companyId, storeId)
      );

      const response = (await fetchGraphQL(
        changePasswordQuery,
        { input },
        setAlert
      )) as ChangePasswordResponse;

      const result = response?.changePassword;

      // ❌ Unexpected response shape: throw so onError handles it
      if (!result) {
        throw new Error(
          "Invalid response structure: missing changePassword field"
        );
      }

      // ⚠️ PASSWORD_INCORRECT: throw so mutateAsync rejects and callers don't proceed
      if (result.code === API_RESPONSE_CODE.PASSWORD_INCORRECT) {
        throw new Error(API_RESPONSE_CODE.PASSWORD_INCORRECT);
      }
      if (result.code !== API_RESPONSE_CODE.OK) {
        throw new Error(String(result.code));
      }

      return response;
    },

    onSuccess: () => {
      // ✅ Success
      sendAppLog(
        LOG_LEVEL.INFO,
        "Password changed successfully",
        buildLogContext("auth", "change-password", userId, companyId, storeId)
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
        code: ALERT_BANNER_CODE.PASSWORD_CHANGED_SUCCESS
      });
    },

    onError: (error, input) => {
      const isIncorrectPassword =
        error.message === API_RESPONSE_CODE.PASSWORD_INCORRECT;

      // ⚠️ PASSWORD_INCORRECT — expected business error
      if (isIncorrectPassword) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Change password failed: incorrect current password provided",
          buildLogContext("auth", "change-password", userId, companyId, storeId)
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.PASSWORD_INCORRECT
        });

        return;
      }

      // ⚠️ Other expected API/business failures, validated against explicit allow-list
      const isKnownApiCode = KNOWN_API_ERROR_CODES.has(error.message);

      if (isKnownApiCode) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Change password rejected by API",
          buildLogContext(
            "auth",
            "change-password",
            userId,
            companyId,
            storeId,
            {
              apiCode: error.message,
              hadCurrentPassword: String(Boolean(input.currentPassword))
            }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code:
            (error.message as unknown as ALERT_BANNER_CODE) ||
            ALERT_BANNER_CODE.INTERNAL_ERROR
        });

        return;
      }

      // ❌ Unexpected runtime/network error or invalid response shape
      captureAppException(
        "Change password failed with unexpected error",
        error,
        buildLogContext("auth", "change-password", userId, companyId, storeId, {
          hadCurrentPassword: String(Boolean(input.currentPassword))
        })
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};

// ─── useUploadProfileImage ────────────────────────────────────────────────────

export const useUploadProfileImage = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const userId = userDetails?.id;
  const companyId = userDetails?.companyId;
  const storeId = userDetails?.storeId;

  return useMutation<UploadProfileImageResponse, Error, File>({
    mutationKey: ["uploadProfileImage"],

    mutationFn: (file) => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started uploading profile image",
        buildLogContext(
          "profile",
          "upload-profile-image",
          userId,
          companyId,
          storeId,
          {
            fileName: file.name,
            fileSize: String(file.size),
            fileType: file.type
          }
        )
      );

      const formData = new FormData();
      formData.append("file", file);

      return fetchGraphQL(
        uploadProfileImageQuery,
        { file: formData },
        setAlert
      );
    }
  });
};
